import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useApi } from '../hooks/useApi';

const RealtimeContext = createContext(null);

const WS_VIEWS = ['org', 'tasks', 'activity', 'decisions', 'settings'];
const RETRY_DELAYS = [1000, 2000, 4000, 8000];

export function RealtimeProvider({ children, auth }) {
  const [data, setData] = useState({});
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [currentTab, setCurrentTab] = useState('tasks');
  const [connected, setConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [stale, setStale] = useState(false);
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const lastMessageRef = useRef(Date.now());
  const staleTimerRef = useRef(null);
  const { get } = useApi();

  const applyDelta = useCallback((view, payload, type) => {
    setData((prev) => {
      const next = { ...prev };
      switch (view) {
        case 'org': {
          if (!next.agents) return next;
          const agents = next.agents.agents.map((a) => {
            if (a.id !== payload.id) return a;
            const updated = { ...a };
            if (type === 'agent.status') updated.status = payload.status;
            if (type === 'agent.inbox') updated.inbox_count = payload.inbox_count;
            if (type === 'agent.queue') updated.queue_depth = payload.queue_depth;
            if (type === 'agent.task') {
              updated.current_task = payload.current_task;
              updated.current_task_title = payload.current_task_title;
            }
            return updated;
          });
          next.agents = { ...next.agents, agents };
          break;
        }
        case 'tasks': {
          if (!next.tasks) return next;
          if (type === 'task.new') {
            const exists = next.tasks.tasks.some((t) => t.id === payload.id);
            if (!exists) {
              next.tasks = { ...next.tasks, tasks: [...next.tasks.tasks, payload] };
            }
          } else if (type === 'task.changed') {
            const tasks = next.tasks.tasks.map((t) => {
              if (t.id !== payload.id) return t;
              return { ...t, ...payload.fields, updated_at: payload.updated_at };
            });
            next.tasks = { ...next.tasks, tasks };
          }
          break;
        }
        case 'activity': {
          if (!next.activity) next.activity = { events: [] };
          const exists = next.activity.events.some((e) => e.id === payload.id);
          if (!exists) {
            next.activity = { ...next.activity, events: [payload, ...next.activity.events] };
          }
          break;
        }
        case 'decisions': {
          if (!next.decisions) next.decisions = { decisions: [] };
          const exists = next.decisions.decisions.some((d) => d.id === payload.id);
          if (!exists) {
            next.decisions = { ...next.decisions, decisions: [payload, ...next.decisions.decisions] };
          }
          break;
        }
        case 'settings': {
          if (type === 'company.mode') {
            next.config = { ...next.config, company_mode: payload.mode };
            next.status = { ...next.status, company_mode: payload.mode };
          } else if (type === 'agent.config') {
            if (!next.config?.roles) return next;
            const roles = { ...next.config.roles };
            roles[payload.id] = { ...roles[payload.id], ...payload.config };
            next.config = { ...next.config, roles };
          }
          break;
        }
        default:
          break;
      }
      return next;
    });
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = new URL('/ws', `${protocol}//${window.location.host}`);
    if (auth.token) wsUrl.searchParams.set('token', auth.token);

    const ws = new WebSocket(wsUrl.toString());
    wsRef.current = ws;

    ws.addEventListener('open', () => {
      setConnected(true);
      setReconnecting(false);
      reconnectAttemptRef.current = 0;
      ws.send(JSON.stringify({ event: 'subscribe', views: WS_VIEWS }));
      WS_VIEWS.forEach((view) => ws.send(JSON.stringify({ event: 'request_sync', view })));
    });

    ws.addEventListener('message', (event) => {
      lastMessageRef.current = Date.now();
      setStale(false);
      try {
        const message = JSON.parse(event.data);
        if (message.event === 'sync' && message.view) {
          const key = message.view === 'settings' ? 'config' : message.view;
          setData((prev) => ({ ...prev, [key]: message.snapshot }));
        } else if (message.view && message.type) {
          applyDelta(message.view, message.payload, message.type);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to parse WebSocket message', err);
      }
    });

    ws.addEventListener('close', () => {
      wsRef.current = null;
      setConnected(false);
      setReconnecting(true);
      const delay = RETRY_DELAYS[Math.min(reconnectAttemptRef.current, RETRY_DELAYS.length - 1)];
      reconnectAttemptRef.current += 1;
      setTimeout(connect, delay);
    });

    ws.addEventListener('error', () => {
      setConnected(false);
    });
  }, [auth.token, applyDelta]);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  useEffect(() => {
    staleTimerRef.current = window.setInterval(() => {
      if (Date.now() - lastMessageRef.current > 30000) {
        setStale(true);
      }
    }, 5000);
    return () => {
      if (staleTimerRef.current) clearInterval(staleTimerRef.current);
    };
  }, []);

  useEffect(() => {
    async function fetchAll() {
      const endpoints = [
        ['status', '/api/status'],
        ['org', '/api/org'],
        ['agents', '/api/agents'],
        ['tasks', '/api/tasks'],
        ['activity', '/api/activity'],
        ['decisions', '/api/decisions'],
        ['escalations', '/api/escalations'],
        ['config', '/api/config'],
      ];
      const results = await Promise.allSettled(endpoints.map(([, path]) => get(path)));
      const next = {};
      endpoints.forEach(([key], index) => {
        const result = results[index];
        if (result.status === 'fulfilled') {
          next[key] = result.value;
        }
      });
      setData((prev) => ({ ...prev, ...next }));
    }
    fetchAll();
  }, [get]);

  const selectAgent = useCallback((id) => {
    setSelectedAgentId(id);
    if (id) setSelectedTaskId(null);
  }, []);

  const selectTask = useCallback((id) => {
    setSelectedTaskId(id);
    if (id) setSelectedAgentId(null);
  }, []);

  const setTab = useCallback((tab) => {
    setCurrentTab(tab);
  }, []);

  const value = useMemo(
    () => ({
      data,
      selectedAgentId,
      selectedTaskId,
      currentTab,
      selectAgent,
      selectTask,
      setTab,
      connection: { connected, reconnecting, stale },
    }),
    [data, selectedAgentId, selectedTaskId, currentTab, selectAgent, selectTask, setTab, connected, reconnecting, stale]
  );

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export function useRealtime() {
  const ctx = useContext(RealtimeContext);
  if (!ctx) throw new Error('useRealtime must be used within RealtimeProvider');
  return ctx;
}
